import React from 'react';
import styled from '@emotion/styled';
import { Sparkles } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation.jsx';

/**
 * The Vipupisteet v2 controls that live in the top bar: which view (Kortit /
 * Kartta / Verkko), which filter (Kaikki / Tarkistamatta / Hyväksytyt), and —
 * for a signed-in facilitator only — the button that runs the discovery.
 *
 * Views are the same pivots drawn three ways; the person stays on this
 * screen and switches. A view that is not built yet is shown disabled with
 * the ticket in its tooltip, never as a link out. `children` is the slot for
 * a view's own toggles (the map's Jäsenet and Seinä).
 */
export const Toggle = styled.button`
  padding: 6px 12px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.14); font: inherit; font-size: 0.8rem; cursor: pointer;
  background: ${(p) => (p.$on ? 'rgba(124,58,237,0.28)' : 'transparent')}; color: ${(p) => (p.$on ? '#fff' : '#b8b8c8')}; font-weight: ${(p) => (p.$on ? 600 : 500)};
  &:hover { color: #fff; }
`;
const Bar = styled.div`display: inline-flex; align-items: center; gap: 10px; flex-wrap: wrap;`;
const Seg = styled.div`display: inline-flex; border: 1px solid rgba(255,255,255,0.14); border-radius: 999px; overflow: hidden;`;
const SegBtn = styled.button`
  padding: 6px 12px; border: 0; background: ${(p) => (p.$on ? 'rgba(124,58,237,0.28)' : 'transparent')};
  color: ${(p) => (p.$on ? '#fff' : '#b8b8c8')}; font: inherit; font-size: 0.8rem; font-weight: ${(p) => (p.$on ? 600 : 500)}; cursor: pointer;
  &:disabled { color: #6b6b7b; cursor: default; }
  & + & { border-left: 1px solid rgba(255,255,255,0.1); }
`;
const Run = styled.button`
  display: inline-flex; align-items: center; gap: 8px; padding: 7px 14px; border-radius: 999px;
  border: 1px solid rgba(167,139,250,0.6); background: rgba(124,58,237,0.22); color: #fff; font: inherit; font-size: 0.85rem; font-weight: 600; cursor: pointer;
  &:disabled { opacity: 0.55; cursor: default; }
`;

// Labels: pivotsV2.toolbar.<key>, hints pivotsV2.toolbar.<titleKey> (locales/pivotsV2Strings.js).
export const VIEWS = [
  { key: 'cards' },
  { key: 'map', titleKey: 'mapHint' },
  { key: 'network', disabled: true, titleKey: 'networkHint' },
];
export const FILTERS = [
  { key: 'all' },
  { key: 'proposed' },
  { key: 'accepted' },
];

export default function PivotToolbar({
  view = 'cards', onViewChange,
  filter = 'all', onFilterChange,
  canRun = false, busy = false, onRun,
  views = VIEWS, filters = FILTERS,
  children = null,
}) {
  const { t } = useTranslation();
  return (
    <Bar>
      {canRun && (
        <Run type="button" onClick={onRun} disabled={busy}><Sparkles size={15} />{busy ? t('pivotsV2.toolbar.reading') : t('pivotsV2.toolbar.find')}</Run>
      )}
      <Seg role="tablist" aria-label={t('pivotsV2.toolbar.viewLabel')}>
        {views.map((v) => (
          <SegBtn key={v.key} type="button" role="tab" aria-selected={view === v.key} $on={view === v.key} disabled={v.disabled} title={v.titleKey ? t(`pivotsV2.toolbar.${v.titleKey}`) : undefined} onClick={() => !v.disabled && onViewChange?.(v.key)}>{t(`pivotsV2.toolbar.${v.key}`)}</SegBtn>
        ))}
      </Seg>
      <Seg role="group" aria-label={t('pivotsV2.toolbar.filterLabel')}>
        {filters.map((f) => (
          <SegBtn key={f.key} type="button" aria-pressed={filter === f.key} $on={filter === f.key} onClick={() => onFilterChange?.(f.key)}>{t(`pivotsV2.toolbar.${f.key}`)}</SegBtn>
        ))}
      </Seg>
      {children}
    </Bar>
  );
}
